import React, { useEffect, useState } from "react";
import {
  ServiceEmailSolicitud,
  ServiceEmailSolicitudDefaults,
  ServiceEmailSolicitudPayload,
} from "../../../models/ServiceEmailSolicitudModel";

type Props = {
  isOpen: boolean;
  item?: ServiceEmailSolicitud | null;
  defaults?: ServiceEmailSolicitudDefaults | null;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (payload: ServiceEmailSolicitudPayload, item?: ServiceEmailSolicitud | null) => void | Promise<void>;
};

type FormState = {
  passenger_count: string;
  passenger_names: string;
  request_date: string;
  request_time: string;
  origin_text: string;
  destination_text: string;
};

const emptyForm = (defaults?: ServiceEmailSolicitudDefaults | null): FormState => ({
  passenger_count: String(defaults?.passenger_count ?? 1),
  passenger_names: defaults?.passenger_names ?? "",
  request_date: defaults?.request_date ?? "",
  request_time: defaults?.request_time ? String(defaults.request_time).slice(0, 5) : "",
  origin_text: defaults?.origin_text ?? "",
  destination_text: defaults?.destination_text ?? "",
});

const ServiceEmailSolicitudModal: React.FC<Props> = ({
  isOpen,
  item,
  defaults,
  loading = false,
  onClose,
  onSubmit,
}) => {
  const [form, setForm] = useState<FormState>(emptyForm(defaults));
  const [submitted, setSubmitted] = useState(false);
  const isEditing = Boolean(item?.id);

  useEffect(() => {
    if (!isOpen) return;
    setSubmitted(false);
    if (item) {
      setForm({
        passenger_count: String(item.passenger_count ?? 1),
        passenger_names: item.passenger_names ?? "",
        request_date: item.request_date ?? "",
        request_time: item.request_time ? String(item.request_time).slice(0, 5) : "",
        origin_text: item.origin_text ?? "",
        destination_text: item.destination_text ?? "",
      });
      return;
    }
    setForm(emptyForm(defaults));
  }, [isOpen, item, defaults]);

  if (!isOpen) return null;

  const updateField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const buildPayload = (): ServiceEmailSolicitudPayload => ({
    passenger_count: Math.max(1, Number(form.passenger_count) || 1),
    passenger_names: form.passenger_names.trim(),
    request_date: form.request_date || null,
    request_time: form.request_time || null,
    origin_text: form.origin_text.trim(),
    destination_text: form.destination_text.trim(),
  });

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    if (!form.passenger_count || Number(form.passenger_count) < 1) return;
    await onSubmit(buildPayload(), item);
  };

  return (
    <div className="services-modal" role="dialog" aria-modal="true" aria-labelledby="service-email-solicitud-title">
      <form className="services-modal-inner service-email-modal" onSubmit={handleSubmit}>
        <header className="services-modal-header">
          <div className="services-modal-title-wrap">
            <h2 id="service-email-solicitud-title" className="services-modal-title">
              {isEditing ? `Editar solicitud #${item?.id}` : "Nueva solicitud"}
            </h2>
          </div>
          <p className="services-modal-subtitle">
            Formulario corto con la información base del correo. Luego podrás vincular uno o más servicios.
          </p>
        </header>

        <div className="services-body service-email-form-grid">
          <div className="service-email-field">
            <label htmlFor="solicitud-passengers">Cant. pasajeros</label>
            <input
              id="solicitud-passengers"
              type="number"
              min="1"
              value={form.passenger_count}
              onChange={(event) => updateField("passenger_count", event.target.value)}
            />
            {submitted && Number(form.passenger_count) < 1 && (
              <span className="service-email-field-error">Ingresa una cantidad válida.</span>
            )}
          </div>

          <div className="service-email-field">
            <label htmlFor="solicitud-pax">Pax</label>
            <input
              id="solicitud-pax"
              type="text"
              value={form.passenger_names}
              onChange={(event) => updateField("passenger_names", event.target.value)}
              placeholder="Nombres de pasajeros"
            />
          </div>

          <div className="service-email-field">
            <label htmlFor="solicitud-date">Día</label>
            <input
              id="solicitud-date"
              type="date"
              value={form.request_date}
              onChange={(event) => updateField("request_date", event.target.value)}
            />
          </div>

          <div className="service-email-field">
            <label htmlFor="solicitud-time">Hora</label>
            <input
              id="solicitud-time"
              type="time"
              value={form.request_time}
              onChange={(event) => updateField("request_time", event.target.value)}
            />
          </div>

          <div className="service-email-field">
            <label htmlFor="solicitud-origin">Origen</label>
            <input
              id="solicitud-origin"
              type="text"
              value={form.origin_text}
              onChange={(event) => updateField("origin_text", event.target.value)}
            />
          </div>

          <div className="service-email-field">
            <label htmlFor="solicitud-destination">Destino</label>
            <input
              id="solicitud-destination"
              type="text"
              value={form.destination_text}
              onChange={(event) => updateField("destination_text", event.target.value)}
            />
          </div>
        </div>

        <footer className="services-footer">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={loading}>
            Cancelar
          </button>
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? "Guardando..." : isEditing ? "Guardar cambios" : "Crear solicitud"}
          </button>
        </footer>
      </form>
    </div>
  );
};

export default ServiceEmailSolicitudModal;
